import { DragDropContext, Draggable, type DropResult, Droppable } from '@hello-pangea/dnd';
import { Box, Stack, Button, TextInput, Divider, Group, useMantineTheme } from '@mantine/core';
import { type Dispatch, type SetStateAction, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { type DefaultSection } from '../../Pages/Settings/ProposalFormatSettings';
import { ThreeDotsMenu } from '../DropdownMenus';
import { AddSubsection, DragAndDrop, Plus, Trash } from '../../icons';
import { removeItem, reorderArray, setItem } from '../../utils/arrayUtils';

interface Props {
  defaultOutline: DefaultSection[]
  setDefaultOutline: Dispatch<SetStateAction<DefaultSection[]>>
}

const DefaultOutlineBuilder: React.FC<Props> = (props: Props) => {
  const { defaultOutline, setDefaultOutline } = props;
  const { colors } = useMantineTheme();
  const updateText = (text: string, index: number) => {
    setDefaultOutline(prev => setItem(prev, index, { ...defaultOutline[index], title: text }));
  };

  const handleUpdateSubsectionText = useCallback((text: string, index: number, subIndex: number) => {
    const subsections = defaultOutline[index].subsections ?? [];
    if (subsections.length < subIndex) {
      return;
    } else {
      subsections[subIndex].title = text;
    }
    setDefaultOutline(prev => setItem(prev, index, { ...defaultOutline[index], subsections }));
  }, [defaultOutline]);

  const handleReorderSubsections = useCallback((sectionId: string, sourceIndex: number, destinationIndex: number) => {
    const sectionIndex = defaultOutline.findIndex((section) => section.id === sectionId);
    const oldSubsections = defaultOutline[sectionIndex].subsections ?? [];
    const subsections = reorderArray(oldSubsections, sourceIndex, destinationIndex);
    setDefaultOutline(prev => setItem(prev, sectionIndex, { ...defaultOutline[sectionIndex], subsections }));
  }, [defaultOutline]);

  const handleDeleteSection = useCallback((index: number) => {
    setDefaultOutline(prev => removeItem(prev, index));
  }, []);

  const handleAddSubsection = useCallback((index: number) => {
    const subsections = [...(defaultOutline[index].subsections ?? []), { title: '', id: uuidv4() }];
    setDefaultOutline(prev => setItem(prev, index, { ...defaultOutline[index], subsections }));
  }, [defaultOutline]);

  const handleDeleteSubsection = useCallback((sectionIndex: number, subsectionIndex: number) => {
    const subsections = removeItem(defaultOutline[sectionIndex].subsections ?? [], subsectionIndex);
    setDefaultOutline(prev => setItem(prev, sectionIndex, { ...defaultOutline[sectionIndex], subsections }));
  }, [defaultOutline]);

  const onAddSection = useCallback(() => {
    setDefaultOutline(prev => [...prev, { title: '', id: uuidv4() }]);
  }, []);

  const getSubsections = (item: DefaultSection) => {
    const index = defaultOutline.findIndex((section) => section.id === item.id);
    return item.subsections?.map((subsection, subIndex) => (
      <Draggable key={subsection.id} index={subIndex} draggableId={subsection.id ?? ''}>
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >
            <Box ml={8} mt={subIndex === 0 ? 0 : 8}>
              <Group gap={0}>
                <DragAndDrop />
                <TextInput
                  placeholder='Subsection Title'
                  value={subsection.title}
                  onChange={(e) => { handleUpdateSubsectionText(e.target.value, index, subIndex); }}
                  flex={1}
                  rightSectionProps={{ style: { borderLeft: `1px solid ${colors.gray[4]}` } }}
                  rightSection={
                    <ThreeDotsMenu
                      menuItems={[
                        {
                          label: 'Delete Subsection',
                          onClick: () => { handleDeleteSubsection(index, subIndex); },
                          shouldRender: true
                        }
                      ]}
                    />
                  }
                />
              </Group>
            </Box>
          </div>
        )}
      </Draggable>
    ));
  };

  const sections = defaultOutline.map((item, index) => {
    const hasSubsections = item.subsections !== undefined && item.subsections.length > 0;
    return (
      <Draggable key={item.id} index={index} draggableId={item.id ?? ''}>
        {(provided, snapshot) => {
          return (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
            >
              <Box p={8} style={{ borderRadius: 8 }} mb={8} bg={colors.greyPurple[1]}>
                <Group gap={0}>
                  <DragAndDrop color='#868E96' />
                  <TextInput
                    placeholder='Section Title'
                    value={item.title}
                    style={{ flex: 1 }}
                    onChange={(event) => { updateText(event.currentTarget.value, index); }}
                    rightSection={
                      <ThreeDotsMenu
                        iconColor='primary'
                        menuItems={[
                          {
                            label: 'Add Subsection',
                            icon: <AddSubsection />,
                            onClick: () => { handleAddSubsection(index); },
                            shouldRender: true
                          },
                          {
                            label: 'Delete Section',
                            icon: <Trash />,
                            onClick: () => { handleDeleteSection(index); },
                            shouldRender: true,
                            color: colors.red[5]
                          }
                        ]}
                      />
                    }
                    rightSectionProps={{ style: { borderLeft: `1px solid ${colors.gray[4]}` } }}
                  />
                </Group>
                {hasSubsections &&
                  <Droppable type='subsection' droppableId={item.id ?? ''} direction="vertical">
                    {(provided, snapshot) => {
                      return (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                          <Group mt={8} ml={16} gap={0}>
                            <Divider orientation='vertical' color={colors.gray[4]} />
                            <Box pr={2} pl={2} flex={1}>
                              {getSubsections(item)}
                            </Box>
                          </Group>
                          {provided.placeholder}
                        </div>
                      );
                    }}
                  </Droppable>
                }
              </Box>
            </div>
          );
        }}
      </Draggable>
    );
  });

  const onDragEnd = (result: DropResult) => {
    if (result.destination === null) {
      return;
    }
    if (result.type === 'section') {
      const { source, destination } = result;
      setDefaultOutline(prev => reorderArray(prev, source.index, destination.index));
    } else if (result.type === 'subsection') {
      const { source, destination } = result;
      if (source.droppableId === destination.droppableId) { // only allow reordering within same section
        handleReorderSubsections(source.droppableId, source.index, destination.index);
      }
    }
  };
  return <>
    <DragDropContext
      onDragEnd={onDragEnd}
    >
      <Stack align='flex-start'>
        <Droppable type='section' droppableId="dnd-list" direction="vertical">
          {(provided) => (
            <Box
              style={{ marginRight: '8px', alignSelf: 'stretch' }}
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {sections}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
        <Button mb={8} onClick={onAddSection} leftSection={<Plus />} variant="outline">
          Add Section
        </Button>
      </Stack>
    </DragDropContext>
  </>;
};

export default DefaultOutlineBuilder;
